/*
 * EmployeeVideos — biblioteca de vídeos de comunicação vista pelo funcionário.
 * Lista o vídeo atual (se houver) + histórico. Cada item pode ser tocado
 * inline. Não bloqueia (o bloqueio acontece no EmployeeVideoBlocker no login).
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Badge } from "./ui/badge";
import { Film, Loader2, CheckCircle2, Circle, Play } from "lucide-react";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      day: "2-digit", month: "2-digit", year: "numeric",
    });
  } catch { return iso; }
};

export default function EmployeeVideos() {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/employee/videos/list`, {
        params: { token }, timeout: 15000,
      });
      setItems(r.data?.items || []);
    } catch { toast.error("Falha ao carregar vídeos."); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8">
          <Loader2 className="w-6 h-6 mx-auto animate-spin text-slate-400" />
        </CardContent>
      </Card>
    );
  }

  if (items.length === 0) {
    return (
      <Card>
        <CardContent className="py-10 text-center">
          <Film className="w-10 h-10 mx-auto text-slate-300 mb-2" />
          <p className="text-sm text-slate-500">
            Nenhum vídeo de comunicação disponível ainda.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white border-slate-200">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center font-heading text-base">
          <Film className="w-4 h-4 mr-2 text-indigo-600" />
          Comunicados em Vídeo
        </CardTitle>
        <CardDescription className="text-xs">
          Vídeos publicados pela AVI — assista quantas vezes quiser
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3" data-testid="employee-videos-list">
          {items.map((v) => (
            <div key={v.id}
                  className="p-3 rounded-lg border border-slate-200 bg-slate-50 hover:bg-slate-100 transition"
                  data-testid={`employee-video-item-${v.id}`}>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="w-16 h-12 bg-slate-900 rounded flex items-center justify-center shrink-0">
                  <Film className="w-5 h-5 text-slate-300" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-800 truncate">
                    {v.title || v.filename}
                  </p>
                  <div className="flex items-center gap-2 mt-0.5 text-[11px] text-slate-500 flex-wrap">
                    <span>{fmtDate(v.created_at)}</span>
                    <span>•</span>
                    <span>{(v.size_bytes / (1024 * 1024)).toFixed(1)} MB</span>
                    {v.computed_status === "running" && (
                      <Badge className="bg-emerald-500 text-white text-[10px]">
                        No ar
                      </Badge>
                    )}
                    {v.acknowledged ? (
                      <span className="flex items-center gap-1 text-emerald-700 font-semibold">
                        <CheckCircle2 className="w-3 h-3" />
                        Ciente registrado
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-slate-500">
                        <Circle className="w-3 h-3" />
                        Ainda não visto
                      </span>
                    )}
                  </div>
                </div>
                <button type="button"
                         onClick={() => setOpenId(openId === v.id ? null : v.id)}
                         data-testid={`employee-video-play-${v.id}`}
                         className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold">
                  <Play className="w-3.5 h-3.5" />
                  {openId === v.id ? "Fechar" : "Assistir"}
                </button>
              </div>
              {openId === v.id && (
                <div className="mt-3 rounded overflow-hidden border border-slate-300 bg-slate-900">
                  <video src={`${API}/employee/videos/${v.id}/stream?token=${encodeURIComponent(token || "")}`}
                          controls playsInline
                          className="w-full max-h-96"
                          data-testid={`employee-video-player-${v.id}`} />
                </div>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
